import { FC, useEffect, useRef } from "react";
import { IconButton } from "@/components/Button";
import { CloseIcon } from "@/components/Icon";
import Alert from "@/components/Alert";
import Typography from "@/components/Typography";
import {
  useTrackers,
  useFeatureFlags,
  useFeatureTraits,
  useParseBannerText,
  useResponsive,
  useOpenExternalURL,
} from "@/hooks";
import styles from "./SuggestionBanner.module.scss";
import theme from "@/theme/theme";
import { useRouter } from "next/router";

const SuggestionContent = () => {
  const { track } = useTrackers();
  const router = useRouter();
  const {
    flags: { eligibleForFullPaymentBanner, eligibleForPaymentPlanBanner },
  } = useFeatureFlags();

  const {
    traits: { bannerAction, bannerText },
  } = useFeatureTraits();

  const goToTripInvoice = () =>
    router.push({
      pathname: "/experience/payments/invoice/trip",
      query: router.query,
    });
  const handleClickPaymentPlan = () => {
    goToTripInvoice();
    track("Payment Plan suggestion Banner Clicked", {
      eventId: "payment-plan-suggestion-banner-clicked",
    });
  };
  const handleClickFullPayment = () => {
    goToTripInvoice();
    track("Full pay suggestion Banner Clicked", {
      eventId: "full-pay-suggestion-banner-clicked",
    });
  };

  const openExternalLink = useOpenExternalURL();

  const handleClickBanner = () => {
    if (bannerAction.indexOf("gworld") > -1) {
      const gwoRldRoute = bannerAction.slice(bannerAction.indexOf(".com") + 4);
      router.push(gwoRldRoute);
    } else {
      openExternalLink(bannerAction);
    }
  };

  const {
    banner: parsedBanner,
    hasBannerVariables,
    day,
    hour,
    minute,
  } = useParseBannerText(bannerText);

  // if (eligibleForPaymentPlanBanner)
  //   return (
  //     <Typography color="text.primary" className={styles.text}>
  //       It is important that you set up your
  //       <Typography role="link" component="a" onClick={handleClickPaymentPlan}>
  //         {" payment plan "}
  //       </Typography>
  //       as soon as possible, so we can secure your spot and get you abroad!
  //     </Typography>
  //   );
  // else if (eligibleForFullPaymentBanner)
  //   return (
  //     <Typography color="text.primary" className={styles.text}>
  //       It is important that you pay your
  //       <Typography role="link" component="a" onClick={handleClickFullPayment}>
  //         {" trip balance "}
  //       </Typography>
  //       in full as soon as you can so we can secure your spot and get you
  //       abroad!
  //     </Typography>
  //   );
  if (bannerText)
    return (
      <Typography
        color="text.primary"
        className={styles.text}
        onClick={handleClickBanner}
      >
        {parsedBanner}
        {hasBannerVariables && (
          <span className={styles.bannerCountDown}>
            {day}d {hour}h {minute}m
          </span>
        )}
      </Typography>
    );
  else return null;
};

const SuggestionBanner: FC<{
  onCloseBanner: () => void;
  showSuggestionBanner: boolean;
}> = ({ onCloseBanner, showSuggestionBanner }) => {
  const { track } = useTrackers();
  const { isMobile } = useResponsive();
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const handleCloseBanner = () => {
    onCloseBanner();
    track("Suggestion Banner Closed", {
      eventId: "suggestion-banner-closed",
    });
  };

  useEffect(() => {
    // Mobile spacing comes from normal document flow. Only the fixed desktop
    // banner needs its height included in the header offset.
    if (isMobile || !showSuggestionBanner || !bannerRef.current) return;

    const banner = bannerRef.current;
    const updateHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--mixins-header-minHeight",
        `calc(${theme.mixins.toolbar.minHeight} + ${banner.clientHeight}px)`
      );
    };

    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(banner);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(
        "--mixins-header-minHeight",
        theme.mixins.toolbar.minHeight
      );
    };
  }, [showSuggestionBanner, isMobile]);

  return showSuggestionBanner ? (
    <Alert
      color="warning"
      icon={false}
      className={styles.root}
      classes={{ message: styles.container, root: styles.paperRoot }}
      ref={bannerRef}
    >
      <SuggestionContent />
      <IconButton className={styles.closeButton} onClick={handleCloseBanner}>
        <CloseIcon className={styles.closeIcon} />
      </IconButton>
    </Alert>
  ) : null;
};

export default SuggestionBanner;
